import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Circle as CircleIcon,
  Hand,
  Highlighter,
  Layers3,
  MessageCircle,
  PenLine,
  Pin,
  RectangleHorizontal,
  Redo2,
  Trash2,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { AppLink } from "@/components/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useDocumentViewport } from "@/features/document-viewport/context";
import {
  clamp,
  ToolColorPicker,
  type Tool,
} from "@/features/rev-collab/rev-collab-session-helpers";
import type { PendingAnnotationDraft } from "@/features/rev-collab/rev-collab-types";
import { getViewportContentCenter } from "@/features/rev-collab/rev-collab-viewport-utils";

const TOOL_BUTTONS: Array<{ value: Tool; label: string; icon: ReactNode }> = [
  { value: "select", label: "Select", icon: <MessageCircle className="size-4" /> },
  { value: "hand", label: "Hand", icon: <Hand className="size-4" /> },
  { value: "highlight", label: "Highlight", icon: <Highlighter className="size-4" /> },
  { value: "rect", label: "Rect", icon: <RectangleHorizontal className="size-4" /> },
  { value: "circle", label: "Circle", icon: <CircleIcon className="size-4" /> },
  { value: "freehand", label: "Pen", icon: <PenLine className="size-4" /> },
  { value: "pin", label: "Pin", icon: <Pin className="size-4" /> },
];

interface RevCollabViewportOverlaysProps {
  tool: Tool;
  setTool: Dispatch<SetStateAction<Tool>>;
  toolColors: Record<Exclude<Tool, "select" | "hand">, string>;
  setToolColors: Dispatch<SetStateAction<Record<Exclude<Tool, "select" | "hand">, string>>>;
  canEditShapes: boolean;
  activePage: number;
  pageCount: number;
  filename: string;
  currentPageCounts: { open: number; resolved: number };
  onlineCount: number;
  takeoffLayerSummary: {
    jobId: Id<"takeoffPilotJobs">;
    jobName: string;
    detectionCount: number;
    measurementCount: number;
  } | null;
  showTakeoffLayer: boolean;
  onShowTakeoffLayerChange: (value: boolean) => void;
  undoCount: number;
  redoCount: number;
  canArchiveSelected: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onArchiveSelected: () => void;
  pendingAnnotationDraft: PendingAnnotationDraft | null;
  pendingCommentBody: string;
  setPendingCommentBody: Dispatch<SetStateAction<string>>;
  onClearPendingDraft: () => void;
  onSavePendingAnnotation: () => void;
  expandedThreadPanelWidth: number;
}

export function RevCollabViewportOverlays({
  tool,
  setTool,
  toolColors,
  setToolColors,
  canEditShapes,
  activePage,
  pageCount,
  filename,
  currentPageCounts,
  onlineCount,
  takeoffLayerSummary,
  showTakeoffLayer,
  onShowTakeoffLayerChange,
  undoCount,
  redoCount,
  canArchiveSelected,
  onUndo,
  onRedo,
  onArchiveSelected,
  pendingAnnotationDraft,
  pendingCommentBody,
  setPendingCommentBody,
  onClearPendingDraft,
  onSavePendingAnnotation,
  expandedThreadPanelWidth,
}: RevCollabViewportOverlaysProps) {
  const viewport = useDocumentViewport();
  const contentCenter = useMemo(() => getViewportContentCenter(viewport), [viewport]);
  const takeoffLayerAvailable = Boolean(
    takeoffLayerSummary &&
      (takeoffLayerSummary.detectionCount > 0 || takeoffLayerSummary.measurementCount > 0)
  );

  const composerPosition = useMemo(() => {
    if (!pendingAnnotationDraft) {
      return null;
    }

    const composerWidth = Math.min(520, viewport.workspaceSize.width - 24);
    const composerHeight = 190;
    const leftLimit = 12;
    const rightLimit = Math.max(
      leftLimit,
      viewport.workspaceSize.width - expandedThreadPanelWidth - composerWidth - 12
    );
    const topLimit = 12;
    const bottomLimit = Math.max(topLimit, viewport.workspaceSize.height - composerHeight - 12);

    return {
      width: composerWidth,
      left: clamp(pendingAnnotationDraft.anchorScreenX + 12, leftLimit, rightLimit),
      top: clamp(pendingAnnotationDraft.anchorScreenY + 12, topLimit, bottomLimit),
    };
  }, [
    expandedThreadPanelWidth,
    pendingAnnotationDraft,
    viewport.workspaceSize.height,
    viewport.workspaceSize.width,
  ]);

  return (
    <>
      <div className="pointer-events-none absolute left-0 right-0 top-4 z-20 flex justify-center px-4">
        <div className="pointer-events-auto flex items-center gap-1 rounded-md border border-border/60 bg-background/90 px-1.5 py-1 shadow-sm backdrop-blur">
          <div className="flex items-center gap-1">
            {TOOL_BUTTONS.map((item) => (
              <Button
                key={item.value}
                type="button"
                variant={tool === item.value ? "default" : "outline"}
                size="icon"
                className="h-8 w-8"
                title={item.label}
                aria-label={item.label}
                onClick={() => setTool(item.value)}
                disabled={!canEditShapes && item.value !== "select" && item.value !== "hand"}
              >
                {item.icon}
              </Button>
            ))}
            {tool !== "select" && tool !== "hand" ? (
              <>
                <div className="mx-1 h-4 w-px bg-border" />
                <ToolColorPicker
                  color={toolColors[tool]}
                  onChange={(color) =>
                    setToolColors((current) => ({ ...current, [tool]: color }))
                  }
                />
              </>
            ) : null}
          </div>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <Button
            type="button"
            variant={showTakeoffLayer && takeoffLayerAvailable ? "default" : "outline"}
            size="sm"
            className="h-8 gap-1.5 px-2 text-xs"
            onClick={() => onShowTakeoffLayerChange(!showTakeoffLayer)}
            disabled={!takeoffLayerAvailable}
            aria-label={showTakeoffLayer ? "Hide TakeoffPilot layer" : "Show TakeoffPilot layer"}
            title={
              takeoffLayerAvailable
                ? `${showTakeoffLayer ? "Hide" : "Show"} read-only TakeoffPilot layer`
                : "No completed takeoff layer for this drawing page"
            }
          >
            <Layers3 className="size-4" />
            <span className="hidden 2xl:inline">Takeoff</span>
          </Button>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              title="Undo"
              aria-label="Undo"
              onClick={onUndo}
              disabled={undoCount === 0}
            >
              <Undo2 className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              title="Redo"
              aria-label="Redo"
              onClick={onRedo}
              disabled={redoCount === 0}
            >
              <Redo2 className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              title="Delete annotation"
              aria-label="Delete annotation"
              onClick={onArchiveSelected}
              disabled={!canArchiveSelected}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => viewport.goToPage(activePage - 1)}
              disabled={activePage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <div className="min-w-14 text-center text-xs tabular-nums text-muted-foreground">
              {activePage}/{pageCount}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => viewport.goToPage(activePage + 1)}
              disabled={activePage >= pageCount}
              aria-label="Next page"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>

          <div className="mx-1.5 h-5 w-px bg-border" />

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => viewport.scaleZoomAt(0.9, contentCenter)}
              aria-label="Zoom out"
            >
              <ZoomOut className="size-4" />
            </Button>
            <div className="min-w-12 text-center text-xs tabular-nums">
              {Math.round(viewport.camera.zoom * 100)}%
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => viewport.scaleZoomAt(1.1, contentCenter)}
              aria-label="Zoom in"
            >
              <ZoomIn className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={viewport.fitWidth}
            >
              Fit W
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={viewport.fitPage}
            >
              Fit P
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => viewport.requestZoomAt(1, contentCenter)}
            >
              100%
            </Button>
          </div>
        </div>
      </div>

      <div
        className="pointer-events-none absolute left-4 z-20"
        style={{
          bottom: canEditShapes ? 16 : 56,
          right: 16,
        }}
      >
        <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap rounded-md border border-border/60 bg-background/90 px-1.5 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
          <Badge variant="outline">{filename}</Badge>
          <Badge variant="secondary">
            {activePage}/{pageCount}
          </Badge>
          <Badge variant="outline">{currentPageCounts.open} open</Badge>
          <Badge variant="outline">{onlineCount} online</Badge>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 hidden -translate-x-1/2 xl:block">
        <div className="pointer-events-auto rounded-2xl border bg-background/95 p-2 shadow-xl backdrop-blur">
          <div className="mb-1 px-2 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
            Pilot glove
          </div>
          <div className="flex items-center gap-1.5">
            <AppLink
              to="/specPilot"
              title="SpecPilot"
              className="rounded-full border border-amber-400/70 bg-amber-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-amber-600 transition-colors hover:bg-amber-400/20"
            >
              Spec
            </AppLink>
            <AppLink
              to="/revPilot"
              title="RevPilot"
              className="rounded-full border border-violet-400/70 bg-violet-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-violet-600 transition-colors hover:bg-violet-400/20"
            >
              Rev
            </AppLink>
            {takeoffLayerSummary ? (
              <AppLink
                to="/takeoffPilot/$jobId"
                params={{ jobId: takeoffLayerSummary.jobId }}
                title={`TakeoffPilot - ${takeoffLayerSummary.jobName}`}
                className="rounded-full border border-sky-400/70 bg-sky-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-sky-600 transition-colors hover:bg-sky-400/20"
              >
                Takeoff
              </AppLink>
            ) : (
              <AppLink
                to="/takeoffPilot"
                title="TakeoffPilot"
                className="rounded-full border border-sky-400/50 bg-sky-400/5 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-sky-600/70 transition-colors hover:bg-sky-400/10"
              >
                Takeoff
              </AppLink>
            )}
            <AppLink
              to="/revCollab"
              title="CollabPilot"
              className="rounded-full border border-emerald-400/70 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-emerald-600 transition-colors hover:bg-emerald-400/20"
            >
              Collab
            </AppLink>
          </div>
        </div>
      </div>

      {pendingAnnotationDraft && composerPosition ? (
        <div className="pointer-events-none absolute inset-0 z-30">
          <Card
            className="pointer-events-auto absolute border border-border/70 bg-background/95 shadow-lg backdrop-blur"
            style={{
              width: composerPosition.width,
              left: composerPosition.left,
              top: composerPosition.top,
            }}
          >
            <CardContent className="space-y-2 p-2">
              <textarea
                autoFocus
                value={pendingCommentBody}
                onChange={(event) => setPendingCommentBody(event.target.value)}
                placeholder="Add comment..."
                rows={5}
                className="h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm outline-none ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && pendingCommentBody.trim()) {
                    event.preventDefault();
                    onSavePendingAnnotation();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    onClearPendingDraft();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline" onClick={onClearPendingDraft}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={onSavePendingAnnotation}
                  disabled={!pendingCommentBody.trim()}
                >
                  Save
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Cmd/Ctrl+Enter to save. Esc to cancel.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {canEditShapes ? null : (
        <div className="pointer-events-none absolute bottom-4 left-4 z-20 rounded-md border bg-background/90 px-3 py-2 text-xs text-muted-foreground backdrop-blur">
          Mobile mode supports viewing and commenting. Annotation editing is desktop/tablet only.
        </div>
      )}
    </>
  );
}
